import api from '../../lib/api';
import { indexedDbPersister } from '../../lib/offline/persister';

/** Session-derived keys that describe *who* is active, not merely that they are. */
const SESSION_DERIVED_KEYS = ['user', 'organization', 'membership', 'availableViews'] as const;

/** Where the admin's own copy of those keys is parked for the duration. */
export const ADMIN_SESSION_SNAPSHOT_KEY = 'originalAdminSession';

/**
 * Snapshot the admin's session-derived state before it is cleared.
 *
 * These four keys are only ever written by setAuthSession(), which runs from a
 * login response — nothing rebuilds them from a bare token. So clearing them on
 * exit left the restored admin session with a valid token and no identity: the
 * header fell back to a "U" avatar and getUserRole() returned its 'user' default,
 * which is what "exiting simulation drops me to a generic user with no admin
 * tools" was. Restoring the snapshot is the counterpart to restoring the token.
 *
 * Never overwrite an existing snapshot — that would capture the *simulated*
 * identity and strand the admin's.
 */
function snapshotAdminSession(): void {
  if (localStorage.getItem(ADMIN_SESSION_SNAPSHOT_KEY)) return;

  const snapshot: Record<string, string> = {};
  for (const key of SESSION_DERIVED_KEYS) {
    const value = localStorage.getItem(key);
    if (value !== null) snapshot[key] = value;
  }
  localStorage.setItem(ADMIN_SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

/**
 * The auth session is more than the token: `user`, `organization`,
 * `membership`, and `availableViews` in localStorage plus the persisted
 * React Query cache all describe whoever was active. Clear them on every
 * simulation transition so the app rebuilds entirely from the active token —
 * otherwise the next session renders the previous identity's role/UI state.
 */
async function clearSessionDerivedState(): Promise<void> {
  for (const key of SESSION_DERIVED_KEYS) {
    localStorage.removeItem(key);
  }
  // Clears the in-memory query cache and starts the IndexedDB wipe…
  window.dispatchEvent(new Event('auth:cleared'));
  // …and await the persisted copy's removal so the full-page reload that
  // follows can't rehydrate stale data before the wipe finishes.
  try {
    await indexedDbPersister.removeClient();
  } catch {
    // best effort — the auth:cleared listener also clears it
  }
}

/**
 * Start a user-simulation (impersonation) session from the admin Accounts
 * tool. Follows the original impersonation localStorage contract that
 * Layout's ImpersonationBanner reads, plus a `returnTo` path so "Exit
 * Simulation" lands back where the session was started.
 *
 * Backend guards (self, other system admins) are enforced by
 * POST /admin/users/:id/impersonate; failures reject with the API error.
 * On success this navigates to /dashboard with a full page reload and does
 * not return.
 */
export async function startUserSimulation(userId: string, returnTo: string): Promise<void> {
  const response = await api.post(`/admin/users/${userId}/impersonate`);
  const { impersonationToken, targetUser, sessionTokenId } = response.data.data;

  // Preserve the admin's own token and account type. Never overwrite them if
  // a simulation is somehow already active — that would strand the real
  // admin's session.
  if (!localStorage.getItem('originalAdminToken')) {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      localStorage.setItem('originalAdminToken', currentToken);
    }
    const currentAccountType = localStorage.getItem('accountType');
    if (currentAccountType) {
      localStorage.setItem('originalAdminAccountType', currentAccountType);
    }
  }

  // Park the admin's identity alongside their token — it is cleared below and
  // nothing can rebuild it from the token alone.
  snapshotAdminSession();

  const targetUserName =
    targetUser.displayName ||
    [targetUser.firstName, targetUser.lastName].filter(Boolean).join(' ') ||
    targetUser.username ||
    'User';

  // Shape read by Layout's impersonation banner (legacy contract) + returnTo.
  localStorage.setItem(
    'impersonation',
    JSON.stringify({
      targetUserName,
      targetUserEmail: targetUser.email,
      sessionTokenId,
      returnTo,
    })
  );

  // Swap in the impersonation token and clear all session-derived state so
  // everything reloads as the target user.
  localStorage.setItem('token', impersonationToken);
  await clearSessionDerivedState();

  // Full page reload so the new token applies everywhere (matches legacy flow).
  window.location.href = '/dashboard';
}
